import React from "react";
import { useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CustomModal from "../../../core/common/customModal";
import { Course, CourseDetailsModalProps } from "../../../core/data/interface";
import { all_routes } from "../../router/all_routes";
import "./courses.css";
import { DEFAULT_THUMBNAIL_URL } from "../../../environment";

// Helper function to count study materials
const countStudyMaterials = (course: Course) => {
  let videos = 0;
  let documents = 0;
  let links = 0;
  let tests = 0;
  
  course.courseDetails.subjects.forEach(subject => {
    subject.syllabus.forEach(syllabus => {
      syllabus.studyMaterials.forEach(material => {
        switch (material.materialType) {
          case 'VIDEO':
            videos++;
            break;
          case 'NOTE':
          case 'FILE':
            documents++;
            break;
          case 'LINK':
            // Check if it's a YouTube link (including YouTube Shorts)
            if (material.content && (material.content.includes('youtube.com') || material.content.includes('youtu.be') || material.content.includes('m.youtube.com'))) {
              videos++;
            } else {
              links++;
            }
            break;
        }
      });
    });
  });
  
  return { videos, documents, links, tests };
};

// Helper function to calculate discount percentage
const calculateDiscountPercentage = (originalPrice: number, promotionalPrice: number) => {
  if (originalPrice <= 0) return 0;
  return Math.round(((originalPrice - promotionalPrice) / originalPrice) * 100);
};

// Helper function to format course type for display
const formatCourseType = (courseType: string) => {
  return courseType.charAt(0).toUpperCase() + courseType.slice(1).toLowerCase();
};


const CourseDetailsModal: React.FC<CourseDetailsModalProps> = ({ course, onClose }) => {
  const navigate = useNavigate();
  
  if (!course) return null;

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star text-warning"></i>);
    }

    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt text-warning"></i>);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star text-warning"></i>);
    }

    return stars;
  };

  // Handle subscribe button click
  const handleSubscribe = (course: Course) => {
    const activeSubscription = course.availableSubscriptions?.find(sub => sub.active);
    
    if (!activeSubscription) {
      console.error('No active subscription found for this course');
      return;
    }

    // Create payment details object
    const paymentDetails = {
      courseId: course.courseId,
      courseTitle: course.courseTitle,
      courseDescription: course.courseDescription,
      courseThumbnail: course.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL,
      subscriptionPlan: {
        subscriptionPlanId: activeSubscription.subscriptionPlanId,
        planName: activeSubscription.planName,
        duration: activeSubscription.duration,
        price: activeSubscription.pricing.price,
        promotionalPrice: activeSubscription.pricing.promotionalPrice,
        currencyCode: activeSubscription.pricing.currencyCode
      },
      totalAmount: activeSubscription.pricing.price,
      discountAmount: activeSubscription.pricing.price - activeSubscription.pricing.promotionalPrice,
      finalAmount: activeSubscription.pricing.promotionalPrice
    };

    // Close modal first
    onClose();
    
    // Navigate to payment page with course details
    navigate(all_routes.order, {
      state: { paymentDetails }
    });
  };

  const materials = countStudyMaterials(course);
  const activeSubscription = course.availableSubscriptions?.find(sub => sub.active);
  const discountPercentage = activeSubscription ? 
    calculateDiscountPercentage(activeSubscription.pricing.price, activeSubscription.pricing.promotionalPrice) : 0;

  const bodyContent = (
    <div className="row">
      {/* Course Image and Basic Info */}
      <div className="col-lg-5">
        <div className="position-relative mb-4">
          <ImageWithBasePath
            src={course.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL}
            alt={course.courseTitle}
            className="img-fluid rounded course-details-image"
          />
          <div className="position-absolute top-0 start-0 p-2">
            <span className="badge bg-primary">{formatCourseType(course.courseType)}</span>
          </div>
          {course.courseMetadata?.difficulty_level && (
            <div className="position-absolute top-0 end-0 p-2">
              <span className="badge bg-info">{course.courseMetadata?.difficulty_level}</span>
            </div>
          )}
        </div>

        {/* Subscription Plans */}
        <div className="mb-4">
          <h6 className="mb-3">{activeSubscription?.planName || 'Batch'}</h6>
          {activeSubscription && (
            <div className="mb-3">
              <div className="p-3 bg-light rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <div className="fw-medium">{activeSubscription.duration ? `${activeSubscription.duration} Months` : 'Lifetime'}</div>
                    <div className="small text-muted">Access Duration</div>
                  </div>
                  <div className="text-end">
                    <div className="d-flex align-items-center">
                      {activeSubscription.pricing.price > 0 && (
                        <span className="text-decoration-line-through text-muted me-2 small">
                          {activeSubscription.pricing.currencyCode} {activeSubscription.pricing.price}
                        </span>
                      )}
                      <span className="h5 fw-bold text-primary mb-0">
                        {activeSubscription.pricing.currencyCode} {activeSubscription.pricing.promotionalPrice}
                      </span>
                    </div>
                    {discountPercentage > 0 && (
                      <div className="small text-success">
                        {discountPercentage}% OFF
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Buy Now Button - Only show for non-enrolled courses */}
              {!course.isEnrolled ? (
                <div className="d-grid mt-3">
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleSubscribe(course)}
                  >
                    <i className="ti ti-shopping-cart me-2"></i>
                    Enroll Now
                  </button>
                </div>
              ) : (
                <div className="d-grid mt-3">
                  <label className="text-success border border-success badge bg-white fw-bold fs-14 p-2">Course Enrolled</label>
                </div>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Course Details */}
      <div className="col-lg-7">
        <h3 className="mb-3">{course.courseTitle}</h3>
        
        {/* Rating and Analytics */}
        <div className="d-flex align-items-center mb-3">
          <div className="me-3">
            {renderStars(course.analytics.averageRating)}
          </div>
          <span className="fw-medium me-3">{course.analytics.averageRating.toFixed(1)}</span>
          <span className="text-muted">({course.analytics.totalReviews} reviews)</span>
        </div>


        {/* Description */}
        <div className="mb-4">
          <h6>Description</h6>
          <p className="text-muted">{course.courseDescription}</p>
        </div>

        {/* Course Materials Details */}
        <div className="mb-4">
          <h6 className="mb-3">Course Materials</h6>
          <div className="row g-3">
            {
              <div className="col-md-4">
              <div className="position-relative p-3 bg-light rounded">
                <div className="d-flex align-items-center">
                  <i className="ti ti-video text-primary me-2" style={{fontSize: '20px'}}></i>
                  <span className="fw-medium">{materials.videos} Video Lessons</span>
                </div>
                <div className="small text-muted">Comprehensive video tutorials covering all topics</div>
              </div>
              </div>
            }
            {
              <div className="col-md-4">
              <div className="position-relative p-3 bg-light rounded">
                <div className="d-flex align-items-center">
                  <i className="ti ti-file-text text-info me-2" style={{fontSize: '20px'}}></i>
                  <span className="fw-medium">{materials.documents + materials.links} Study Materials</span>
                </div>
                <div className="small text-muted">PDFs, guides, and reference documents</div>
              </div>
              </div>
            }
            {
              <div className="col-md-4">
              <div className="position-relative p-3 bg-light rounded">
                <div className="d-flex align-items-center">
                  <i className="ti ti-clipboard-check text-success me-2" style={{fontSize: '20px'}}></i>
                  <span className="fw-medium">{course.assessmentCount} Tests</span>
                </div>
                <div className="small text-muted">Interactive assessments and quizzes</div>
              </div>
              </div>
            }
          </div>
        </div>
        {/* Course Subjects */}
        {course.courseDetails.subjects.length > 0 && (
          <div className="mb-4">
            <h6>Course Subjects</h6>
            <div className="row g-2 mt-1">
              {course.courseDetails.subjects.map(subject => (
                <div key={subject.subjectId} className="col-md-6">
                  <div className="p-2 border rounded">
                    <div className="fw-medium">{subject.subjectTitle}</div>
                    <small className="text-muted">{subject.subjectDescription}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* What You'll Learn */}
        { course.courseMetadata?.learning_highlights?.length && <div className="mb-4">
          <h6>What You'll Learn</h6>
          <ul className="list-unstyled mt-1">
          {course.courseMetadata?.learning_highlights?.map((highlight) => (
            <li className="mb-2">
              <i className="ti ti-check text-success me-2"></i>
              {highlight}
            </li>))}
          </ul>
        </div>}
      </div>
    </div>
  );

  const footerContent = (
    <div className="d-flex justify-content-end w-100">
      <button
        type="button"
        className="btn btn-light"
        onClick={onClose}
      >
        Close
      </button>
    </div>
  );

  return (
    <CustomModal
      show={!!course}
      title="Course Details"
      onClose={onClose}
      bodyContent={bodyContent}
      footerContent={footerContent}
      size="xl"
      backdrop="static"
    />
  );
};

export default CourseDetailsModal;